import { CenteredContent } from '@/shared/ui/layout/CenteredContent';
import { SectionWrapper } from '@/entities/widget';
import { Button, Spinner } from '@monksflow/monks-ui';
import { useEffect, useState, useMemo } from 'react';
import { FourCsWidgetSchemaType } from '@inference.engine/widgets';
import { BorderedBox } from '@/shared/ui/BorderedBox';
import { useSidePanelScroll } from '@/shared/ui/SidePanelWrapper/SidePanelScrollContext';
import { FOUR_CS_KEYS } from '../../model/four-cs-widget.constants';
import type { FourCsKey } from '../../model/four-cs-widget.types';
import { useTranslation } from 'react-i18next';

function hasData(c: {
  guidingQuestions: string[];
  findings: string[];
  implications: unknown[];
  truths: unknown[];
}): boolean {
  return (
    c.guidingQuestions.length > 0 ||
    c.findings.length > 0 ||
    c.implications.length > 0 ||
    c.truths.length > 0
  );
}

export const FourCs = ({ data }: { data: FourCsWidgetSchemaType }) => {
  const { t } = useTranslation('analysis');

  const { scrollToTop } = useSidePanelScroll();
  useEffect(() => {
    scrollToTop();
  }, [scrollToTop]);

  const cStates = useMemo(
    () => ({
      category: hasData(data.fourCs.category),
      consumer: hasData(data.fourCs.consumer),
      company: hasData(data.fourCs.company),
      cultural: hasData(data.fourCs.cultural),
    }),
    [data.fourCs],
  );

  const loadingC = useMemo(() => {
    if (!cStates.category) return 'category';
    if (!cStates.consumer) return 'consumer';
    if (!cStates.company) return 'company';
    if (!cStates.cultural) return 'cultural';
    return null;
  }, [cStates]);

  const [selectedKey, setSelectedKey] = useState<FourCsKey>('category');
  const currentCategory = data.fourCs[selectedKey];

  return (
    <CenteredContent>
      <SectionWrapper compact>
        <div className="flex flex-col gap-2">
          <p className="text-heading-xl text-foreground">{t('fourCsWidget.fourCs.reviewThe4CS')}</p>
          <p className="my-6 text-body-lg text-muted-foreground">
            {t('fourCsWidget.fourCs.hereAreTheKeyFindings')}
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-8">
          {FOUR_CS_KEYS.map(({ key, label }) => {
            const isLoaded = cStates[key];
            const isLoading = loadingC === key;
            const isSelected = selectedKey === key && isLoaded;

            return (
              <Button
                key={key}
                variant={isSelected ? 'secondary' : 'outline'}
                aria-pressed={isSelected}
                aria-busy={isLoading}
                onClick={() => isLoaded && setSelectedKey(key)}
                disabled={!isLoaded}
              >
                {isLoading && <Spinner aria-hidden />}
                {label}
              </Button>
            );
          })}
        </div>
      </SectionWrapper>
      <SectionWrapper title={t('fourCsWidget.fourCs.guidingQuestions')}>
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {currentCategory.guidingQuestions.length === 0 ? (
            <div className="flex flex-row gap-2">
              <p className="text-body-lg text-foreground">
                {t('fourCsWidget.fourCs.noGuidingQuestionsFound')}
              </p>
            </div>
          ) : (
            currentCategory.guidingQuestions.map((guidingQuestion, index) => (
              <div
                key={index}
                className="flex flex-row gap-2 rounded-lg p-4 flex-start bg-orange-50"
              >
                <p className="text-heading-md text-foreground">{guidingQuestion}</p>
              </div>
            ))
          )}
        </div>
      </SectionWrapper>
      <SectionWrapper title={t('fourCsWidget.fourCs.findings')}>
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {currentCategory.findings.length === 0 ? (
            <div className="flex flex-row gap-2">
              <p className="text-body-lg text-foreground">
                {t('fourCsWidget.fourCs.noFindingsFound')}
              </p>
            </div>
          ) : (
            currentCategory.findings.map((finding, index) => {
              const badgeNumber = index + 1;
              return (
                <BorderedBox key={index} className="flex flex-row gap-2">
                  <span
                    className={`inline-flex shrink-0 items-center justify-center rounded-full bg-orange-50 text-body-lg text-orange-900 ${
                      badgeNumber >= 10 ? 'size-8' : 'size-7'
                    }`}
                  >
                    {badgeNumber}
                  </span>
                  <p className="text-body-lg text-foreground">{finding}</p>
                </BorderedBox>
              );
            })
          )}
        </div>
      </SectionWrapper>
      <SectionWrapper title={t('fourCsWidget.fourCs.implications')}>
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {currentCategory.implications.length === 0 ? (
            <div className="flex flex-row gap-2">
              <p className="text-body-lg text-foreground">
                {t('fourCsWidget.fourCs.noImplicationsFound')}
              </p>
            </div>
          ) : (
            currentCategory.implications.map((implication, index) => (
              <div key={index} className="rounded-lg p-4 bg-orange-50">
                <div>
                  <p className="text-body-lg font-medium text-orange-900">{implication.topic}</p>
                </div>
                <p className="text-body-lg text-foreground">{implication.description}</p>
              </div>
            ))
          )}
        </div>
      </SectionWrapper>
    </CenteredContent>
  );
};
